import Reveal from "@/components/Reveal";
import { OFFICES } from "@/data/content";
import { trackEvent } from "@/lib/tracking";
import { MapPin, Phone, Mail } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const mapSrc = (address) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`;

function MapFrame({ office, className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-black/10 shadow-sm bg-black/5 ${className}`}>
      <iframe
        title={`Map of ${office.name}`}
        data-testid={`map-${slug(office.name)}`}
        src={mapSrc(office.address)}
        className="absolute inset-0 w-full h-full"
        style={{ border: 0 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

function OfficeDetails({ office, large = false }) {
  return (
    <div data-testid={`office-${slug(office.name)}`}>
      <h3 className={`font-heading font-black text-rc-blue leading-tight ${large ? "text-3xl" : "text-xl"}`}>
        {office.name}
      </h3>
      <div className="mt-3 space-y-2 text-rc-brown text-sm">
        <div className="flex items-start gap-2.5">
          <MapPin size={15} className="mt-0.5 shrink-0 text-rc-red" /> {office.address}
        </div>
        <div className="flex items-center gap-2.5 font-mono">
          <Phone size={14} className="shrink-0 text-rc-red" /> {office.phone}
        </div>
        {office.email && (
          <a
            href={`mailto:${office.email}`}
            data-testid={`office-email-${slug(office.name)}`}
            className="flex items-center justify-center sm:justify-start gap-2.5 text-rc-red font-mono hover:underline break-all"
          >
            <Mail size={14} className="shrink-0" /> {office.email}
          </a>
        )}
      </div>
    </div>
  );
}

export default function Locations({ showEstimating = true }) {
  const hq = OFFICES.find((o) => o.hq) || OFFICES[0];
  const branches = OFFICES.filter((o) => !o.hq);

  return (
    <section data-testid="locations-section" className="bg-rc-bg py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {showEstimating && <Reveal>
          <div
            data-testid="estimating-callout"
            className="bg-white rounded-2xl border border-black/10 shadow-sm p-5 sm:p-7 lg:p-10 mb-16 max-w-3xl mx-auto text-center"
          >
            <div className="font-mono text-rc-red text-xs uppercase tracking-[0.3em] mb-4">For Estimating</div>
            <h2 className="font-heading font-black text-rc-blue text-[clamp(1.6rem,3.5vw,2.4rem)] leading-tight">
              For quotes, bids, or inquiries, please email us.
            </h2>
            <a
              href="mailto:estimating@rangerconstruction.com"
              data-testid="estimating-email-link"
              onClick={() => trackEvent("cta_click", { label: "estimating_email" })}
              className="mt-6 inline-flex max-w-full items-center justify-center gap-2 bg-rc-red text-white px-3 sm:px-7 py-3.5 rounded-full font-bold tracking-normal text-[13px] sm:text-sm hover:bg-rc-reddark transition-colors"
            >
              <Mail size={16} className="shrink-0" /><span className="min-w-0"><span className="whitespace-nowrap">estimating@</span><wbr /><span className="whitespace-nowrap">rangerconstruction.com</span></span>
            </a>
          </div>
        </Reveal>}

        <Reveal className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-20">
          <div>
            <div className="font-mono text-rc-red text-xs uppercase tracking-[0.3em] mb-5">Headquarters</div>
            <OfficeDetails office={hq} large />
          </div>
          <MapFrame office={hq} className="h-[340px] lg:h-[380px]" />
        </Reveal>

        <div className="font-mono text-rc-red text-xs uppercase tracking-[0.3em] mb-8">Regional Offices</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {branches.map((o) => (
            <Reveal key={o.name} className="flex flex-col">
              <MapFrame office={o} className="h-[220px]" />
              <div className="mt-6">
                <OfficeDetails office={o} />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
